import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CreditCard, Zap, Star, Building, Check, Loader, ShieldCheck, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentGate = () => {
  const navigate = useNavigate();
  const { user, token, login } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // If already paid and has credits, redirect to portal
  useEffect(() => {
    if (user?.has_paid) {
      // Check if user has credits
      const checkCredits = async () => {
        try {
          const res = await axios.get(`${API}/billing/balance`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.credits > 0) navigate('/portal', { replace: true });
        } catch {}
      };
      checkCredits();
    }
  }, [user, token, navigate]);

  // Check for returning from Stripe (session_id in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId || !token) return;

    const pollPayment = async () => {
      try {
        const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.payment_status === 'paid') {
          // Refresh user data to get has_paid=true
          const meRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          login(token, meRes.data);
          navigate('/portal', { replace: true });
        }
      } catch {}
    };
    pollPayment();
  }, [token, navigate, login]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/billing/packages`);
        setPackages(res.data.packages || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handlePurchase = async (pkgId) => {
    setPurchasing(pkgId);
    try {
      const res = await axios.post(`${API}/billing/purchase-credits`, {
        package_id: pkgId,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Redirect to Stripe checkout
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Er ging iets mis');
      setPurchasing(null);
    }
  };

  const icons = { starter: Zap, pro: Star, business: Building, enterprise: ShieldCheck };

  if (loading) return (
    <div className="auth-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader size={32} className="spin" />
    </div>
  );

  return (
    <div className="auth-page" data-testid="payment-gate">
      <div style={{ maxWidth: 900, width: '95vw', margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 style={{ fontSize: '1.2rem', letterSpacing: 3, color: '#999', marginBottom: '0.75rem' }}>KRYPTON9</h1>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#F4F7FB', marginBottom: '0.5rem' }}>
            {user?.has_paid ? 'Credits bijkopen' : 'Kies je startpakket'}
          </h2>
          <p style={{ fontSize: '1rem', color: '#666', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
            {user?.has_paid
              ? 'Je credits zijn op. Koop een nieuw pakket om verder te gaan met alle AI-tools en Emma.'
              : `Welkom${user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! Je gratis credits zijn op. Kies een creditpakket om verder te gaan.`
            }
          </p>
        </div>

        {/* Packages grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem', marginBottom: '2rem',
        }}>
          {(Array.isArray(packages) ? packages : []).map(pkg => {
            const Icon = icons[pkg.id] || CreditCard;
            const isPopular = pkg.popular;
            return (
              <div key={pkg.id} data-testid={`package-${pkg.id}`} style={{
                background: '#0E1620', borderRadius: 14, padding: '1.75rem 1.5rem',
                border: isPopular ? '2px solid #1a1a1a' : '1px solid #e5e5e5',
                position: 'relative', display: 'flex', flexDirection: 'column',
                boxShadow: isPopular ? '0 8px 32px rgba(0,0,0,0.1)' : 'none',
                transform: isPopular ? 'scale(1.03)' : 'none',
              }}>
                {isPopular && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: '#1a1a1a', color: '#fff', padding: '3px 14px', borderRadius: 20,
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1,
                  }}>POPULAIR</div>
                )}
                <Icon size={24} style={{ color: '#F4F7FB', marginBottom: '0.75rem' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.25rem' }}>{pkg.name}</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.75rem', fontWeight: 800 }}>€{pkg.price}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '1rem', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Check size={14} style={{ color: '#16a34a' }} /> {pkg.credits} credits
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Check size={14} style={{ color: '#16a34a' }} /> €{pkg.per_credit.toFixed(3)}/credit
                  </div>
                </div>
                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing !== null}
                  data-testid={`buy-${pkg.id}`}
                  style={{
                    width: '100%', padding: '0.7rem', borderRadius: 8, border: 'none',
                    background: isPopular ? '#1a1a1a' : '#16202C',
                    color: isPopular ? '#fff' : '#F4F7FB',
                    fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {purchasing === pkg.id ? <Loader size={16} className="spin" /> : <CreditCard size={16} />}
                  {purchasing === pkg.id ? 'Even geduld...' : 'Kiezen'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust indicators */}
        <div style={{ textAlign: 'center', color: '#999', fontSize: '0.8rem', marginBottom: '2rem' }}>
          <p>Veilig betalen via Stripe — iDEAL, creditcard of Google Pay</p>
        </div>

        {/* Account verwijderen link */}
        <div style={{ textAlign: 'center', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <button onClick={() => setShowDeleteModal(true)} data-testid="gate-delete-btn" style={{
            background: 'none', border: 'none', color: '#999', fontSize: '0.8rem',
            cursor: 'pointer', textDecoration: 'underline',
          }}>
            Account verwijderen
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }} data-testid="gate-delete-modal">
          <div style={{
            background: '#0E1620', borderRadius: 16, padding: '2rem', maxWidth: 480, width: '90vw',
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
              <div style={{ background: '#16202C', borderRadius: '50%', padding: 10 }}>
                <AlertTriangle size={24} style={{ color: '#dc2626' }} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#F4F7FB' }}>Account Verwijderen?</h3>
            </div>
            <p style={{ fontSize: '0.925rem', lineHeight: 1.6, color: '#555', marginBottom: '1.25rem' }}>
              Weet je zeker dat je je account wilt verwijderen? <strong>Alle data</strong> inclusief je AI-assistent <strong>Emma</strong> worden permanent verwijderd.
            </p>
            <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: '0.75rem' }}>
              Typ <strong>VERWIJDEREN</strong> om te bevestigen:
            </p>
            <input type="text" value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="VERWIJDEREN" className="form-input" data-testid="gate-delete-input"
              style={{ marginBottom: '1.25rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                className="btn-secondary">Annuleren</button>
              <button
                disabled={deleteConfirmText !== 'VERWIJDEREN' || deleting}
                data-testid="gate-delete-confirm"
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/auth/account`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    localStorage.clear();
                    window.location.href = '/';
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Er ging iets mis');
                    setDeleting(false);
                  }
                }}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
                  background: deleteConfirmText === 'VERWIJDEREN' ? '#dc2626' : '#16202C',
                  color: deleteConfirmText === 'VERWIJDEREN' ? '#fff' : '#999',
                  cursor: deleteConfirmText === 'VERWIJDEREN' ? 'pointer' : 'not-allowed',
                  fontWeight: 600, fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {deleting ? <Loader size={16} className="spin" /> : <Trash2 size={16} />}
                {deleting ? 'Verwijderen...' : 'Definitief Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGate;
